<template>
    <p-form @submit="onSubmit">
        <div class="container-fluid content-wrapper" v-if="product">
            <b-row class="content-heading">
                <b-col>
                    <div>{{ isNew ? 'Add' : 'Edit' }} Book</div>
                </b-col>
                <b-col class="text-end">
                    <p-button type="submit" :is-busy="isBusy" variant="primary"
                        >Save</p-button
                    >
                </b-col>
            </b-row>
            <b-tabs justified id="variants" v-model="tabIndex">
                <b-tab title="Book Details">
                    <NewUsedBookTab
                        :product="product"
                        :parameters="this.parameters"
                        :is-new="isNew"
                        @deleteProduct="deleteProduct"
                        @savePage="onPrintLabelClicked"
                        @noDigitalIsbnFound="clearDirtyFlag"
                    />
                </b-tab>
                <b-tab title="Rentals" v-if="product.rentalInfo && product.rentalInfo.length > 0">
                    <RentalsTab
                        :product="product"
                        :rental-info="product.rentalInfo"
                    />
                </b-tab>
                <b-tab title="Digital" v-if="product['digitalVariant']">
                    <DigitalTab
                        :product="product"
                        :parameters="this.parameters"
                        @deleteProduct="deleteProduct"
                        @clearDirtyFlag="clearDirtyFlag"
                    />
                </b-tab>
                <b-tab title="Info">
                    <InfoTab :productInfo="product.productInfo" />
                </b-tab>
            </b-tabs>
            <b-row class="mt-4">
                <b-col class="text-center mb-3">
                    <p-button
                        variant="primary"
                        type="submit"
                        :is-busy="isBusy"
                        class="text-end"
                        >Save</p-button
                    >
                </b-col>
            </b-row>
        </div>
    </p-form>
</template>

<script>
import axios from 'axios';
import NavigationGuard from '@/components/mixins/NavigationGuard.js';
import NewUsedBookTab from './NewUsedBookTab.vue';
import DigitalTab from './DigitalTab.vue';
import RentalsTab from './RentalsTab.vue';
import InfoTab from './ProductInfoTab.vue';
import selectListOptionsDataContext from "@/services/selectListOptions.dataContext.js";

export default {
    mixins: [NavigationGuard],
    props: {
        id: String
    },
    data() {
        return {
            product: null,
            parameters: null,
            barcodes: null,
            isBusy: false,
            type: 2,
            tabIndex: 0,
            bookVariants: ['new', 'used', 'digital', 'trade'],
            isPrintLabelClicked: false,
            productType: String
        };
    },
    watch: {
        product: NavigationGuard.$watcher,
        $route: function(to, from) {
            if (to !== from) {
                this.loadData();
            }
        }
    },
    computed: {
        isNew() {
            return this.id === 'new';
        },
        activeVariants() {
            return this.bookVariants
                .filter(x => this.product[`${x}Variant`] != null)
                .map(x => this.product[`${x}Variant`]);
        }
    },
    methods: {
        deleteProduct({ variant, type }) {
            if (type == 'new' && this.product.usedVariant != null) {
                this.$appError(
                    'Must delete used book before deleting new.'
                );
                return;
            } 
            let formDirtyIsBeforeDelete = this.formIsDirty;
            this.$bvModal
                .msgBoxConfirm(
                    'Are you sure you want to delete this product?',
                    {
                        okTitle: 'Delete',
                        okVariant: 'danger'
                    }
                )
                .then(value => {
                    if (value) {
                        this.isBusy = true; 
                        axios
                            .post('products/delete', {sku:[variant.sku]}) 
                            .then(resp => {
                                if (resp) {
                                    this.product[`${type}Variant`] = null;
                                    // When the digital variant is deleted we need to remove it from the UI (its type is 'trade' so we have to remove the digital variant explicitly)
                                    if(this.product.digitalVariant) {
                                        this.product.digitalVariant = null;
                                    }                                    
                                    this.$appSuccess(
                                            `SKU ${variant.sku} deleted successfully.`
                                        )
                                    if (this.activeVariants.length < 1) {
                                        // Nulling the variant(s) above mutates the deep-watched
                                        // product, so the nav-guard watcher flips formIsDirty back
                                        // on — but it runs asynchronously, after this block. Defer
                                        // clearing the flag + navigating to the next tick so it
                                        // happens after that watcher, otherwise the unsaved-changes
                                        // guard prompts on leave.
                                        this.$nextTick(() => {
                                            this.dataSaved();
                                            this.$router.push('/products');
                                        });
                                    } else if (!formDirtyIsBeforeDelete) {
                                        this.dataLoaded();
                                    }
                                }
                            })
                            .finally(() => {
                                this.isBusy = false;
                            });
                    }
                });
        },

        onSubmit() {
            if (
                this.product.newVariant == null &&
                this.product.usedVariant == null &&
                this.product.tradeVariant == null &&
                this.product.digitalVariant == null
            ) {
                this.$appError(
                    'You must add a new, used, trade, or digital variant before saving.'
                );
                this.isPrintLabelClicked = false;
                return;
            }
            if (
                (this.product.newVariant &&
                    this.product.newVariant.cost == null) ||
                (this.product.tradeVariant &&
                    this.product.tradeVariant.cost == null)
            ) {
                this.$appError(
                    'You must add a cost before saving.'
                );
                this.isPrintLabelClicked = false;
                return;
            }
            if (
                (this.product.newVariant &&
                    this.product.newVariant.margin == null) ||
                (this.product.tradeVariant &&
                    this.product.tradeVariant.margin == null)
            ) {
                this.$appError(
                    'You must add a margin before saving.'
                );
                this.isPrintLabelClicked = false;
                return;
            }
            if (
                (this.product.newVariant &&
                    this.product.newVariant.retail == null) ||
                (this.product.tradeVariant &&
                    this.product.tradeVariant.retail == null)
            ) {
                this.$appError(
                    'You must add a retail before saving.'
                );
                this.isPrintLabelClicked = false;
                return;
            }

            let hasZeroCost = this.bookVariants.some(
                v => this.product[`${v}Variant`]?.cost === 0
            );
            if (hasZeroCost) {
                this.$bvModal
                    .msgBoxConfirm(
                        'About to save item with a $0.00 cost. Do you wish to continue?'
                    )
                    .then(value => {
                        if (!value) {
                            return;
                        }
                        this.saveProduct();
                        return;
                    });
            } else {
                this.saveProduct();
                return;
            }

            // todo error handling
        },

        async saveProduct() {
            //submit product save after validation has passed

            this.isBusy = true;
            this.product.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();
            // Toggle any null (from a Check Digital addition) to booleans before submitting
            if (this.product.digitalVariant) {
                this.product.digitalVariant.isActive = !!this.product.digitalVariant.isActive;
                this.product.digitalVariant.iaeaOnly = !!this.product.digitalVariant.iaeaOnly;
            }

            axios
                .post('products/tx', this.product)
                .then(response => {
                    if (!this.isNew) {
                        this.product = response.data;
                    }                    
                    if (this.isNew) {
                        let sku = response.data.newVariant
                            ? response.data.newVariant.sku
                            : response.data.usedVariant
                                ? response.data.usedVariant.sku
                                : response.data.tradeVariant
                                    ? response.data.tradeVariant.sku
                                    : response.data.digitalVariant.sku;
                        this.dataSaved();
                        this.$router.push('/products/tx/' + sku);
                    }
                    this.$appSuccess(
                            `Product '${this.product.title}' saved successfully.`
                        )
                })
                .finally(() => {
                    this.isBusy = false;
                    if(this.isPrintLabelClicked)this.$bvModal.show('printLabelModal'+this.productType);
                    this.isPrintLabelClicked = false;
                    this.dataSaved();
                });
        },
        async loadData() {            
            let locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();

            let dataPromise = this.isNew
                ? axios.get('products/book/new')
                : axios.get('products/book', { params: { id: this.id, locationId: locationId } });
            let paramPromise = axios.get('products/bookparameters');
            await axios.all([dataPromise, paramPromise]).then(
                axios.spread(async (dataResp, paramResp) => {
                    this.parameters = paramResp.data;
                    this.product = dataResp.data;

                    if (this.product.digitalVariant?.sku == this.id) {
                        this.tabIndex = this.product.rentalInfo && this.product.rentalInfo.length > 0 ? 2 : 1;
                    }

                    this.barcodes = {};
                    if (this.product.newVariant != null) {
                        this.barcodes.newTextbook = this.product.newVariant.barcodes;
                    }
                    if (this.product.usedVariant != null) {
                        this.barcodes.usedTextbook = this.product.usedVariant.barcodes;
                    }
                    if (this.product.tradeVariant != null) {
                        this.barcodes.tradebook = this.product.tradeVariant.barcodes;
                    }
                    if (!this.isNew) {
                        if (this.product.type != this.type) {
                            this.$router.push('/products');
                        }
                    }
                    if (this.isNew) {
                        this.product.bindingId =
                            paramResp.data.defaultBindingId;
                        //await this.addVariant('new');
                    }

                    if (!this.isNew) {
                            this.dataLoaded();
                    }
                })
            );
        },
        onPrintLabelClicked(type){
            if(type)
            {
                this.productType = type;
                if (!this.formIsDirty){
                    this.$bvModal.show('printLabelModal'+this.productType);
                }else{
                    this.isPrintLabelClicked = true;
                    this.onSubmit();
                }
            }else{
                if(this.formIsDirty)
                {
                    this.onSubmit();
                }
            }
        },
        clearDirtyFlag(){
            this.dataSaved();
        }
    },
    mounted: async function() {
        await this.loadData();
    },
    components: {
        DigitalTab,
        NewUsedBookTab,
        RentalsTab,
        InfoTab
    }
};
</script>
